<template>
  <div class="container mx-auto px-4 py-8">
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-bold text-gray-900 dark:text-white">Documents</h1>
      <button
        @click="openCreateModal"
        class="btn-primary"
      >
        Add Document
      </button>
    </div>

    <DataTable
      :columns="columns"
      resourceName="documents"
      @rowClick="showDocumentDetails"
    >
      <template #cell="{ column, value }">
        <template v-if="column.key === 'is_public'">
          <div class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" :class="value ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'">
            <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path v-if="value" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
            </svg>
            {{ value ? 'Public' : 'Private' }}
          </div>
        </template>
        <template v-else-if="column.key === 'needs_approval'">
          <div class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" :class="value ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'">
            <svg class="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path v-if="value" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
            {{ value ? 'Pending Approval' : 'Approved' }}
          </div>
        </template>
        <template v-else>
          {{ value }}
        </template>
      </template>
    </DataTable>

    <!-- Add/Edit Document Modal -->
    <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" @click.self="showModal = false">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div class="p-6">
          <h2 class="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
            Add New Document
          </h2>
          <form @submit.prevent="handleSubmit" class="space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
              <input
                v-model="form.name"
                type="text"
                required
                class="input mt-1"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Title</label>
              <input
                v-model="form.title"
                type="text"
                required
                class="input mt-1"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
              <textarea
                v-model="form.description"
                rows="3"
                class="input mt-1"
              ></textarea>
            </div>

            <!-- Document Upload Section -->
            <div class="space-y-4">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Document Source</label>
              <div class="flex space-x-4">
                <button
                  type="button"
                  @click="handleUploadMethodChange('url')"
                  class="flex-1 px-4 py-2 rounded-md border transition-colors duration-200"
                  :class="[
                    uploadMethod === 'url'
                      ? 'bg-primary/10 border-primary text-primary dark:bg-primary-light/10 dark:border-primary-light dark:text-primary-light'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                  ]"
                >
                  <div class="flex items-center justify-center space-x-2">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    <span>Document URL</span>
                  </div>
                </button>
                <button
                  type="button"
                  @click="handleUploadMethodChange('file')"
                  class="flex-1 px-4 py-2 rounded-md border transition-colors duration-200"
                  :class="[
                    uploadMethod === 'file'
                      ? 'bg-primary/10 border-primary text-primary dark:bg-primary-light/10 dark:border-primary-light dark:text-primary-light'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                  ]"
                >
                  <div class="flex items-center justify-center space-x-2">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span>Upload File</span>
                  </div>
                </button>
              </div>

              <!-- URL Input -->
              <div v-if="uploadMethod === 'url'" class="mt-4">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Document URL</label>
                <input
                  v-model="form.document_url"
                  type="url"
                  required
                  placeholder="https://example.com/document.pdf"
                  class="input mt-1"
                />
              </div>

              <!-- File Upload -->
              <div v-else class="mt-4">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Upload Document</label>
                <div class="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-md">
                  <div class="space-y-1 text-center">
                    <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <div class="flex text-sm text-gray-600 dark:text-gray-400">
                      <label class="relative cursor-pointer rounded-md font-medium text-primary dark:text-primary-light hover:text-primary-dark dark:hover:text-primary focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary">
                        <span>Upload a file</span>
                        <input
                          ref="fileInput"
                          type="file"
                          class="sr-only"
                          @change="handleFileChange"
                          accept=".pdf,.docx,.json,.txt"
                        />
                      </label>
                      <p class="pl-1">or drag and drop</p>
                    </div>
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      PDF, DOCX, JSON, or TXT up to 100MB
                    </p>
                    <p v-if="form.file" class="text-sm text-primary dark:text-primary-light">
                      Selected file: {{ form.file.name }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Category Selection -->
            <div class="mt-4">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Linked Category</label>
              <select
                v-model="form.linked_with_category"
                class="input mt-1"
              >
                <option :value="0">None</option>
                <option
                  v-for="category in activeCategories"
                  :key="category.id"
                  :value="category.id"
                >
                  {{ category.name }}
                </option>
              </select>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Optionally link this document to a security category
              </p>
            </div>

            <div class="flex items-center space-x-4">
              <label class="flex items-center">
                <input
                  v-model="form.is_public"
                  type="checkbox"
                  class="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Public Document</span>
              </label>
              <label class="flex items-center">
                <input
                  v-model="form.needs_approval"
                  type="checkbox"
                  class="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Needs Approval</span>
              </label>
            </div>

            <div class="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                @click="showModal = false"
                class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                :disabled="isUploading"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="isUploading || (uploadMethod === 'url' && !form.document_url) || (uploadMethod === 'file' && !form.file)"
              >
                <span v-if="isUploading" class="flex items-center">
                  <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Uploading...
                </span>
                <span v-else>Add Document</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- View/Edit Document Modal -->
    <div v-if="selectedDocument" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" @click.self="closeModal">
      <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div class="p-6">
          <div class="flex justify-between items-start mb-6">
            <h2 class="text-xl font-semibold text-gray-900 dark:text-white">Edit Document</h2>
            <button 
              @click="closeModal" 
              class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none"
            >
              <span class="sr-only">Close</span>
              <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form @submit.prevent="saveDocument" class="space-y-4">
            <!-- Basic Information -->
            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
              <input
                v-model="selectedDocument.name"
                type="text"
                required
                class="input mt-1"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Title</label>
              <input
                v-model="selectedDocument.title"
                type="text"
                required
                class="input mt-1"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
              <textarea
                v-model="selectedDocument.description"
                rows="3"
                class="input mt-1"
              ></textarea>
            </div>

            <!-- Document Source Section -->
            <div class="space-y-4">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Document Source</label>
              <div class="flex space-x-4">
                <button
                  type="button"
                  @click="handleEditUploadMethodChange('url')"
                  class="flex-1 px-4 py-2 rounded-md border transition-colors duration-200"
                  :class="[
                    editUploadMethod === 'url'
                      ? 'bg-primary/10 border-primary text-primary dark:bg-primary-light/10 dark:border-primary-light dark:text-primary-light'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                  ]"
                >
                  <div class="flex items-center justify-center space-x-2">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                    </svg>
                    <span>Document URL</span>
                  </div>
                </button>
                <button
                  type="button"
                  @click="handleEditUploadMethodChange('file')"
                  class="flex-1 px-4 py-2 rounded-md border transition-colors duration-200"
                  :class="[
                    editUploadMethod === 'file'
                      ? 'bg-primary/10 border-primary text-primary dark:bg-primary-light/10 dark:border-primary-light dark:text-primary-light'
                      : 'border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                  ]"
                >
                  <div class="flex items-center justify-center space-x-2">
                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                    </svg>
                    <span>Upload File</span>
                  </div>
                </button>
              </div>

              <!-- URL Input -->
              <div v-if="editUploadMethod === 'url'" class="mt-4">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Document URL</label>
                <input
                  v-model="selectedDocument.document_url"
                  type="url"
                  placeholder="https://example.com/document.pdf"
                  class="input mt-1"
                />
              </div>

              <!-- File Upload -->
              <div v-else class="mt-4">
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Document File</label>
                <!-- Show current file if exists -->
                <div v-if="selectedDocument?.file_url" class="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-2">
                      <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      <span class="text-sm text-gray-600 dark:text-gray-300">{{ selectedDocument.file_name }}</span>
                    </div>
                    <div class="flex items-center space-x-2">
                    <a
                      :href="selectedDocument.file_url"
                      target="_blank"
                      class="inline-flex items-center px-2.5 py-1.5 text-sm font-medium text-primary dark:text-primary-light hover:text-primary-dark dark:hover:text-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-primary-light"
                    >
                      <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Download
                    </a>
                      <button
                        type="button"
                        @click="showFileUpload = true"
                        class="inline-flex items-center px-2.5 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-800"
                      >
                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        Change File
                      </button>
                    </div>
                  </div>
                </div>
                <!-- File Upload Section -->
                <div v-if="!selectedDocument?.file_url || showFileUpload" class="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 dark:border-gray-600 border-dashed rounded-md">
                  <div class="space-y-1 text-center">
                    <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                      <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <div class="flex text-sm text-gray-600 dark:text-gray-400">
                      <label class="relative cursor-pointer rounded-md font-medium text-primary dark:text-primary-light hover:text-primary-dark dark:hover:text-primary focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary">
                        <span>Upload a file</span>
                        <input
                          ref="editFileInput"
                          type="file"
                          class="sr-only"
                          @change="handleEditFileChange"
                          accept=".pdf,.docx,.json,.txt"
                        />
                      </label>
                      <p class="pl-1">or drag and drop</p>
                    </div>
                    <p class="text-xs text-gray-500 dark:text-gray-400">
                      PDF, DOCX, JSON, or TXT up to 100MB
                    </p>
                    <p v-if="editFile" class="text-sm text-primary dark:text-primary-light">
                      Selected file: {{ editFile.name }}
                    </p>
                    <button
                      v-if="showFileUpload"
                      type="button"
                      @click="showFileUpload = false"
                      class="mt-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Category Selection -->
            <div class="mt-4">
              <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Linked Category</label>
              <select
                v-model="selectedCategoryId"
                class="input mt-1"
              >
                <option :value="0">None</option>
                <option
                  v-for="category in activeCategories"
                  :key="category.id"
                  :value="category.id"
                >
                  {{ category.name }}
                </option>
              </select>
              <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Optionally link this document to a security category
              </p>
            </div>

            <!-- Flags -->
            <div class="flex items-center space-x-4">
              <label class="flex items-center">
                <input
                  v-model="selectedDocument.is_public"
                  type="checkbox"
                  class="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Public Document</span>
              </label>
              <label class="flex items-center">
                <input
                  v-model="selectedDocument.needs_approval"
                  type="checkbox"
                  class="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Needs Approval</span>
              </label>
            </div>

            <!-- Action Buttons -->
            <div class="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                @click="deleteDocument(selectedDocument)"
                class="px-4 py-2 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                :disabled="isUploading"
              >
                Delete
              </button>
              <button
                type="button"
                @click="closeModal"
                class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                :disabled="isUploading"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="isUploading"
              >
                <span v-if="isUploading" class="flex items-center">
                  <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
                <span v-else>Save Changes</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <DeleteConfirmationModal
      :show="showDeleteModal"
      title="Confirm Delete Document"
      message="Are you sure you want to delete this document? This action cannot be undone."
      :loading="isDeleting"
      @confirm="handleDeleteConfirm"
      @cancel="showDeleteModal = false"
    />
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount, computed } from 'vue'
import { useStore } from 'vuex'
import DataTable from '@/components/DataTable.vue'
import DeleteConfirmationModal from '@/components/DeleteConfirmationModal.vue'

export default {
  name: 'DocumentsView',
  components: {
    DataTable,
    DeleteConfirmationModal
  },
  setup() {
    const store = useStore()
    const showModal = ref(false)
    const showDeleteModal = ref(false)
    const selectedDocument = ref(null)
    const documentToDelete = ref(null)
    const selectedCategoryId = ref(0)
    const showFileUpload = ref(false)
    const isDeleting = ref(false)
    let modalTimeout = null

    const form = ref({
      name: '',
      title: '',
      description: '',
      is_public: false,
      needs_approval: false,
      document_url: '',
      file: null,
      linked_with_category: 0
    })

    const uploadMethod = ref('url') // 'url' or 'file'
    const fileInput = ref(null)
    const isUploading = ref(false)

    const editUploadMethod = ref('url')
    const editFile = ref(null)
    const editFileInput = ref(null)

    const columns = [
      { key: 'id', label: 'ID', class: 'w-16' },
      { key: 'title', label: 'Title' },
      { key: 'name', label: 'Name' },
      { key: 'description', label: 'Description' },
      { key: 'is_public', label: 'Visibility', class: 'w-32' },
      { key: 'needs_approval', label: 'Status', class: 'w-32' },
      { key: 'updated_at', label: 'Last Updated', class: 'w-40' }
    ]

    const formatDate = (date) => {
      if (!date) return ''
      try {
        return new Date(date).toLocaleString()
      } catch (error) {
        console.error('Error formatting date:', error)
        return ''
      }
    }

    const openCreateModal = async () => {
      showModal.value = true
      store.dispatch('securityCategories/fetchData', {
        tenantId: store.state.currentTenantId,
        page: 1,
        entriesPerPage: 100,
        search: null,
        orderBy: null,
        sortDirection: null
      })
    }

    const showDocumentDetails = async (document) => {
      try {
        selectedDocument.value = { ...document }
        selectedCategoryId.value = document.linked_with_category || 0
        editUploadMethod.value = document.document_url ? 'url' : 'file'
        editFile.value = null
        
        // If document has a file, get its URL
        if (!document.document_url) {
          try {
            const fileData = await store.dispatch('getDocumentFileURL', {
              tenantId: store.state.currentTenantId,
              documentId: document.id
            })
            selectedDocument.value = {
              ...selectedDocument.value,
              file_url: fileData.url,
              file_name: fileData.file?.name || 'Document'
            }
          } catch (error) {
            console.error('Error fetching document file:', error)
          }
        }
        
        // Always fetch categories when opening the modal
        await store.dispatch('securityCategories/fetchData', {
          tenantId: store.state.currentTenantId,
          page: 1,
          entriesPerPage: 100,
          search: null,
          orderBy: null,
          sortDirection: null
        })
      } catch (error) {
        console.error('Error in showDocumentDetails:', error)
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: 'Failed to load document details',
          duration: 3000
        })
      }
    }

    const closeModal = () => {
      selectedDocument.value = null
      selectedCategoryId.value = 0
      editUploadMethod.value = 'url'
      editFile.value = null
      showFileUpload.value = false
      if (editFileInput.value) {
        editFileInput.value.value = ''
      }
    }

    const loadDocuments = async () => {
      try {
        const response = await store.dispatch('fetchDocuments', store.state.currentTenantId)
        store.commit('setDocuments', response)
      } catch (error) {
        console.error('Error loading documents:', error)
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: 'Failed to refresh documents list',
          duration: 3000
        })
      }
    }

    const saveDocument = async () => {
      if (!selectedDocument.value) return
      
      try {
        isUploading.value = true
        // Show loading notification
        store.dispatch('notification/showNotification', {
          type: 'info',
          message: 'Saving document...',
          duration: 1000
        })

        // Create a plain object with the document data
        const documentData = {
          title: selectedDocument.value.title,
          name: selectedDocument.value.name,
          description: selectedDocument.value.description,
          is_public: selectedDocument.value.is_public,
          needs_approval: selectedDocument.value.needs_approval,
          linked_with_category: selectedCategoryId.value === 0 ? null : selectedCategoryId.value
        }

        // Update the document
        await store.dispatch('documents/updateData', {
          tenantId: store.state.currentTenantId,
          id: selectedDocument.value.id,
          data: documentData
        })

        // If there's a new file, upload it
        if (editFile.value) {
          await store.dispatch('uploadDocumentFile', {
            tenantId: store.state.currentTenantId,
            documentId: selectedDocument.value.id,
            file: editFile.value
          })
        }

        // Refresh the documents list
        await loadDocuments()

        // Show success notification
        store.dispatch('notification/showNotification', {
          type: 'success',
          message: 'Document updated successfully',
          duration: 3000
        })

        closeModal()
      } catch (error) {
        console.error('Error saving document:', error)
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: error.response?.data?.message || 'Failed to save document',
          duration: 5000
        })
      } finally {
        isUploading.value = false
      }
    }

    const deleteDocument = (document) => {
      documentToDelete.value = document
      showDeleteModal.value = true
    }

    const handleDeleteConfirm = async () => {
      if (!documentToDelete.value) return

      try {
        isDeleting.value = true
        await store.dispatch('documents/deleteData', {
          tenantId: store.state.currentTenantId,
          id: documentToDelete.value.id
        })

        // Refresh the documents list after successful deletion
        await loadDocuments()

        store.dispatch('notification/showNotification', {
          type: 'success',
          message: 'Document deleted successfully',
          duration: 3000
        })

        if (selectedDocument.value?.id === documentToDelete.value.id) {
          closeModal()
        }

        showDeleteModal.value = false
        documentToDelete.value = null
      } catch (error) {
        console.error('Error deleting document:', error)
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: error.response?.data?.message || 'Failed to delete document',
          duration: 5000
        })
      } finally {
        isDeleting.value = false
      }
    }

    const handleSubmit = async () => {
      try {
        isUploading.value = true
        // Show loading notification
        store.dispatch('notification/showNotification', {
          type: 'info',
          message: 'Creating document...',
          duration: 1000
        })

        // Create document data
        const documentData = {
          title: form.value.title,
          name: form.value.name,
          description: form.value.description,
          is_public: form.value.is_public,
          needs_approval: form.value.needs_approval,
          document_url: uploadMethod.value === 'url' ? form.value.document_url : '',
          linked_with_category: form.value.linked_with_category === 0 ? null : form.value.linked_with_category
        }

        // Create the document
        await store.dispatch('createDocumentWithFile', {
          tenantId: store.state.currentTenantId,
          documentData,
          file: uploadMethod.value === 'file' ? form.value.file : null
        })

        // Refresh the documents list
        await loadDocuments()

        // Show success notification
        store.dispatch('notification/showNotification', {
          type: 'success',
          message: 'Document created successfully',
          duration: 3000
        })

        // Reset form and close modal
        form.value = {
          name: '',
          title: '',
          description: '',
          is_public: false,
          needs_approval: false,
          document_url: '',
          file: null,
          linked_with_category: 0
        }
        uploadMethod.value = 'url'
        if (fileInput.value) {
          fileInput.value.value = ''
        }
        showModal.value = false
      } catch (error) {
        console.error('Error creating document:', error)
        store.dispatch('notification/showNotification', {
          type: 'error',
          message: error.response?.data?.message || 'Failed to create document',
          duration: 5000
        })
      } finally {
        isUploading.value = false
      }
    }

    const handleFileChange = (event) => {
      const file = event.target.files[0]
      if (file) {
        form.value.file = file
      }
    }

    const handleUploadMethodChange = (method) => {
      uploadMethod.value = method
      form.value.document_url = ''
      form.value.file = null
      if (fileInput.value) {
        fileInput.value.value = ''
      }
    }

    const handleEditFileChange = (event) => {
      const file = event.target.files[0]
      if (file) {
        editFile.value = file
      }
    }

    const handleEditUploadMethodChange = (method) => {
      editUploadMethod.value = method
      selectedDocument.value.document_url = ''
      editFile.value = null
      if (editFileInput.value) {
        editFileInput.value.value = ''
      }
    }

    const categories = computed(() => store.state.securityCategories.data || [])
    const activeCategories = computed(() =>
      categories.value.filter(c =>
        c.sub_categories && c.sub_categories.some(sc => sc.is_enabled)
      )
    )

    onMounted(() => {
      // The DataTable component will handle loading the documents
    })

    onBeforeUnmount(() => {
      if (modalTimeout) {
        clearTimeout(modalTimeout)
      }
    })

    return {
      columns,
      showModal,
      showDeleteModal,
      selectedDocument,
      documentToDelete,
      selectedCategoryId,
      categories,
      activeCategories,
      openCreateModal,
      showDocumentDetails,
      closeModal,
      saveDocument,
      deleteDocument,
      handleDeleteConfirm,
      formatDate,
      loadDocuments,
      form,
      handleSubmit,
      uploadMethod,
      fileInput,
      isUploading,
      isDeleting,
      handleFileChange,
      handleUploadMethodChange,
      editUploadMethod,
      editFile,
      editFileInput,
      handleEditUploadMethodChange,
      handleEditFileChange,
      showFileUpload
    }
  }
}
</script>

<style scoped>
.container {
  @apply max-w-7xl mx-auto;
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.15s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
</style>